/**
 * Data Context Service
 * Aggregates all system data to provide contextual information to the AI chatbot
 */

/**
 * Get comprehensive system data context for AI chatbot
 * @returns {Object} Structured data context with all system metrics
 */
export function getSystemDataContext() {
  return {
    businessAnchors: {
      totalRevenue: { value: "$24.8M", trend: "+12.5%" },
      totalCost: { value: "$18.2M", trend: "+8.3%" },
      grossProfit: { value: "$6.6M", trend: "+18.2%" },
      profitMargin: { value: "26.6%", trend: "+4.2%" }
    },
    enterpriseHealth: {
      overallPerformance: { status: "Excellent", description: "All key metrics trending positively with strong execution" },
      businessHealth: { status: "Healthy", description: "Strong fundamentals with minor areas for improvement" }
    },
    efficiency: {
      costVsPerformance: { value: "1.42", trend: "+0.15" },
      valueLeakage: { value: "$1.2M", trend: "-18.5%" }
    },
    risk: {
      executionRisk: { status: "Moderate Risk", description: "3 projects showing delay indicators, monitoring required" },
      businessStability: { status: "Stable", description: "Consistent performance with low volatility" }
    },
    aiInsights: {
      performance: {
        executionHealthScore: { score: 87, maxScore: 100, description: "Strong execution across all business units with minimal delays" },
        executionVariance: { score: 5.2, maxScore: 10, description: "Low variance indicates consistent delivery performance" },
        executionRiskScore: { score: 23, maxScore: 100, description: "Low risk profile with proactive mitigation strategies in place" },
        performanceDegradation: { score: 12, maxScore: 100, description: "Minimal degradation detected, systems operating optimally" }
      },
      financial: {
        costLeakage: { score: 3.8, maxScore: 10, description: "Minimal cost leakage identified, strong financial controls" },
        highCostOutliers: { score: 2, maxScore: 10, description: "Few outliers detected, cost distribution is healthy" },
        costStability: { score: 91, maxScore: 100, description: "Highly stable cost structure with predictable patterns" }
      },
      workforce: {
        lowPerformerRatio: { score: 8, maxScore: 100, description: "Low percentage of underperformers, strong team capability" },
        attritionRisk: { score: 15, maxScore: 100, description: "Moderate attrition risk in specific departments, action plans active" }
      },
      meta: {
        decisionConfidence: { score: 89, maxScore: 100, description: "High confidence in data quality and analytical insights for decision-making" }
      }
    },
    revenueDrivers: {
      byProgram: [
        { program: "Enterprise Solutions", revenue: "$4.25M", growth: "+12.5%" },
        { program: "Cloud Services", revenue: "$3.8M", growth: "+18.3%" },
        { program: "Consulting", revenue: "$2.9M", growth: "+8.7%" },
        { program: "Support & Maintenance", revenue: "$2.1M", growth: "+5.2%" },
        { program: "Training Services", revenue: "$1.45M", growth: "-2.1%" }
      ],
      topContributors: [
        { name: "Global Tech Corp", revenue: "$2.85M", percentage: "19.7%", trend: "up" },
        { name: "Innovation Systems Ltd", revenue: "$2.34M", percentage: "16.1%", trend: "up" },
        { name: "Digital Ventures Inc", revenue: "$1.92M", percentage: "13.2%", trend: "stable" },
        { name: "Enterprise Solutions Co", revenue: "$1.68M", percentage: "11.6%", trend: "up" },
        { name: "Cloud Partners LLC", revenue: "$1.45M", percentage: "10.0%", trend: "down" }
      ],
      lowRevenueAreas: [
        { area: "Legacy Product Line", revenue: "$450K", decline: "-15.3%", issue: "Product obsolescence and declining market demand" },
        { area: "Regional Office - Southeast", revenue: "$680K", decline: "-8.7%", issue: "High competition and pricing pressure" },
        { area: "Small Business Segment", revenue: "$520K", decline: "-12.1%", issue: "Low conversion rates and high customer acquisition costs" }
      ]
    },
    executionMetrics: {
      projectHealth: {
        onTrack: 45,
        atRisk: 8,
        delayed: 3,
        completed: 127
      },
      deliveryPerformance: {
        onTimeDelivery: "87%",
        averageDelay: "3.2 days",
        qualityScore: "92/100"
      }
    },
    resourceMetrics: {
      workforce: {
        totalEmployees: 1247,
        utilization: "82%",
        topPerformers: "15%",
        atRisk: "8%"
      },
      skills: {
        criticalGaps: ["Cloud Architecture", "Data Science", "Cybersecurity"],
        trainingInProgress: 156
      }
    },
    costDrivers: {
      byCategory: [
        { category: "Personnel", cost: "$12.5M", percentage: "68.7%" },
        { category: "Infrastructure", cost: "$3.2M", percentage: "17.6%" },
        { category: "Operations", cost: "$1.8M", percentage: "9.9%" },
        { category: "Marketing", cost: "$0.7M", percentage: "3.8%" }
      ],
      highCostAreas: [
        { area: "Legacy System Maintenance", cost: "$850K", issue: "High maintenance costs for outdated systems" },
        { area: "Overtime & Contractors", cost: "$620K", issue: "Resource constraints leading to overtime" }
      ]
    },
    profitDrivers: {
      margins: {
        gross: "26.6%",
        operating: "18.3%",
        net: "12.7%"
      },
      profitability: {
        highMarginProducts: ["Enterprise Solutions", "Cloud Services"],
        lowMarginProducts: ["Training Services", "Legacy Products"]
      }
    },
    systemIntegrations: [
      { system: "Salesforce CRM", status: "Active", lastSync: "2 mins ago" },
      { system: "SAP ERP", status: "Active", lastSync: "5 mins ago" },
      { system: "Workday HCM", status: "Active", lastSync: "3 mins ago" },
      { system: "Jira Project Management", status: "Active", lastSync: "1 min ago" },
      { system: "AWS Cloud Services", status: "Active", lastSync: "4 mins ago" }
    ],
    lastUpdated: "Feb 10, 2026"
  };
}

/**
 * Format system data context into a concise summary for AI (optimized for token efficiency)
 * @returns {string} Formatted context string
 */
export function formatSystemContextForAI() {
  const data = getSystemDataContext();
  
  return `You are an AI assistant for a Business Analytics Dashboard with access to real-time data (${data?.lastUpdated}).

KEY METRICS:
Revenue: ${data?.businessAnchors?.totalRevenue?.value} (${data?.businessAnchors?.totalRevenue?.trend}) | Cost: ${data?.businessAnchors?.totalCost?.value} (${data?.businessAnchors?.totalCost?.trend}) | Profit: ${data?.businessAnchors?.grossProfit?.value} (${data?.businessAnchors?.grossProfit?.trend}) | Margin: ${data?.businessAnchors?.profitMargin?.value}

HEALTH: ${data?.enterpriseHealth?.overallPerformance?.status} | Risk: ${data?.risk?.executionRisk?.status}

TOP PROGRAMS: ${data?.revenueDrivers?.byProgram?.slice(0, 3)?.map(p => `${p?.program} ${p?.revenue}`)?.join(' | ')}

PROJECTS: ${data?.executionMetrics?.projectHealth?.onTrack} on-track, ${data?.executionMetrics?.projectHealth?.atRisk} at-risk, ${data?.executionMetrics?.projectHealth?.delayed} delayed

WORKFORCE: ${data?.resourceMetrics?.workforce?.totalEmployees} employees, ${data?.resourceMetrics?.workforce?.utilization} utilization

Provide concise, data-driven insights. Reference actual numbers when answering.`;
}

export default {
  getSystemDataContext,
  formatSystemContextForAI
};